import {ref} from 'vue';
import {subYears} from 'date-fns';
import {defineStore} from 'pinia';
import {readonlyState} from '@malt/utils';
import {Configuration, type MinMaxRuleResource, PublicValidationRulesApi, type RuleResourcesInner, RuleResourcesInnerNameEnum} from '@api';
import type {EditExperience} from '~/features/experience/types/EditExperience';

export type ProfileRuleMinMax = {
    min: number;
    max: number;
};

function ruleResourceInnerToProfileRuleMinMax(ruleResource: MinMaxRuleResource): ProfileRuleMinMax {
    return {
        min: ruleResource.min,
        max: ruleResource.max,
    };
}

export const useRulesStore = defineStore('profile-rules', () => {
    // Profile picture
    const profilePictureWidthHeight = ref<ProfileRuleMinMax>({min: 400, max: 500});
    const profilePictureFormats = ref({fileTypes: ['image/jpeg', 'image/jpg', 'image/png'], formats: ['JPEG', 'PNG']});
    const profilePictureFileSize = ref<ProfileRuleMinMax>({min: 0, max: 5});

    // Education
    const educationCountForMaltStrategy = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const educationDegreeLength = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const educationSchoolLength = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const educationGraduationYear = ref<ProfileRuleMinMax>({min: new Date().getFullYear() - 80, max: new Date().getFullYear()});

    // Experiences
    const experienceDescriptionLength = ref<ProfileRuleMinMax>({min: 100, max: 2000});
    const experienceTitleLength = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const experienceClientCompanyLength = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const experienceMinCount = ref<ProfileRuleMinMax>({min: 1, max: 1000});
    const experienceMaxSkills = ref<number>(5);

    // Industry expertise
    const industryExpertisesCount = ref<ProfileRuleMinMax>({min: 1, max: 5});

    // Skill Set
    const skillSetFunctionalSkills = ref<ProfileRuleMinMax>({min: 0, max: 50});
    const skillSetGlobalSkillLabel = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const skillSetGlobalSkills = ref<ProfileRuleMinMax>({min: 0, max: 50});
    const skillSetSelectedSkills = ref<ProfileRuleMinMax>({min: 4, max: 50});
    const skillSetTopSkills = ref<ProfileRuleMinMax>({min: 0, max: 5});
    const skillSetSkills = ref<ProfileRuleMinMax>({min: 0, max: 50});

    // Portfolio
    const portfolioDescriptionLength = ref<ProfileRuleMinMax>({min: 10, max: 200});
    const portfolioTitleLength = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const portfolioMaxSkills = ref<number>(5);

    // Others
    const alternativeAddressesCount = ref<ProfileRuleMinMax>({min: 0, max: 4});
    const categoryOther = ref<ProfileRuleMinMax>({min: 1, max: 100});
    const certificationSkills = ref<ProfileRuleMinMax>({min: 0, max: 10});
    const dailyRatePrice = ref<ProfileRuleMinMax>({min: 125, max: 5000});
    const jobTitle = ref<ProfileRuleMinMax>({min: 5, max: 50});
    const languageCount = ref<ProfileRuleMinMax>({min: 1, max: 50});
    const resumeDescriptionLength = ref<ProfileRuleMinMax>({min: 200, max: 2000});
    const travelLocationsCount = ref<ProfileRuleMinMax>({min: 0, max: 5});

    const _apiConfiguration = new Configuration({basePath: ''});
    const _publicValidationRulesApi = new PublicValidationRulesApi(_apiConfiguration);
    const _hasBeenFetched = ref<boolean>(false);

    async function init(params: {profileId?: string | undefined; vertical?: 'FREELANCER' | 'STRATEGY' | undefined}) {
        if (!_hasBeenFetched.value) {
            const rulesResources = await _publicValidationRulesApi.rules({profileId: params.profileId, vertical: params.vertical});
            rulesResources.forEach((ruleResourcesInner) => setProfileMinMaxRule(ruleResourcesInner));
            _hasBeenFetched.value = true;
        }

        return {
            certificationSkills: certificationSkills.value,
            dailyRatePrice: dailyRatePrice.value,
            experienceDescriptionLength: experienceDescriptionLength.value,
            experienceTitleLength: experienceTitleLength.value,
            experienceMinCount: experienceMinCount.value,
            resumeDescriptionLength: resumeDescriptionLength.value,
            skillSetFunctionalSkills: skillSetFunctionalSkills.value,
            skillSetGlobalSkillLabel: skillSetGlobalSkillLabel.value,
            skillSetSelectedSkills: skillSetSelectedSkills.value,
            skillSetTopSkills: skillSetTopSkills.value,
        };
    }

    function setProfileMinMaxRule(ruleResourcesInner: RuleResourcesInner) {
        switch (ruleResourcesInner.name) {
            // A-Z
            case RuleResourcesInnerNameEnum.CertificationSkills:
                certificationSkills.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.DailyRatePrice:
                dailyRatePrice.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.EducationCountStrategy:
                educationCountForMaltStrategy.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.EducationDegreeLength:
                educationDegreeLength.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.EducationGraduationYearRange:
                educationGraduationYear.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.EducationSchoolLength:
                educationSchoolLength.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.FunctionalSkills:
                skillSetFunctionalSkills.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.GlobalSkillsLength:
                skillSetGlobalSkillLabel.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.GlobalSkills:
                skillSetGlobalSkills.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.HeadlineLength:
                jobTitle.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.IndustryExpertises:
                industryExpertisesCount.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.LanguagesCount:
                languageCount.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.OtherCategoryLength:
                categoryOther.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.ResumeDescriptionLength:
                resumeDescriptionLength.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.Skills:
                skillSetSelectedSkills.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.TopSkills:
                skillSetTopSkills.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.WorkExperienceClientCompanyLength:
                experienceClientCompanyLength.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.WorkExperienceCount:
                experienceMinCount.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.WorkExperienceDescriptionLength:
                experienceDescriptionLength.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
            case RuleResourcesInnerNameEnum.WorkExperienceTitleLength:
                experienceTitleLength.value = ruleResourceInnerToProfileRuleMinMax(ruleResourcesInner.minMaxRule);
                break;
        }
    }

    /** Minimal fixed date for both start and end dates */
    const experienceFixedDateMin = ref(subYears(new Date(), 100));
    const experienceFixedDateMax = ref(new Date());

    function validateExperienceDescription(description?: string): boolean {
        return _validate(experienceDescriptionLength.value, description);
    }

    function validateExperienceCompany(company?: string): boolean {
        return _validate(experienceClientCompanyLength.value, company);
    }

    function isValidExperienceDateRange(startDate?: Date, endDate?: Date, currentJob?: boolean): boolean {
        if (!startDate || (!endDate && !currentJob)) {
            return false;
        }

        if (!isValidExperienceStartDate(startDate) || !isValidExperienceEndDate(endDate, currentJob)) {
            return false;
        }

        if (!currentJob && startDate > endDate!) {
            return false;
        }

        return true;
    }

    function isValidExperienceEndDate(endDate?: Date, currentJob?: boolean): boolean {
        return !!currentJob || (!!endDate && endDate < experienceFixedDateMax.value && endDate > experienceFixedDateMin.value);
    }

    function isValidExperienceStartDate(startDate?: Date): boolean {
        return !!startDate && startDate > experienceFixedDateMin.value && startDate < experienceFixedDateMax.value;
    }

    function isValidExperienceTitle(title?: string): boolean {
        return _validate(experienceTitleLength.value, title);
    }

    function missingOrTooShort(rule: ProfileRuleMinMax, value?: string): boolean {
        return !value || value.length < rule.min;
    }

    function tooLong(rule: ProfileRuleMinMax, value?: string): boolean {
        return !!value && value.length > rule.max;
    }

    function _validate(rule: ProfileRuleMinMax, value?: string): boolean {
        return !missingOrTooShort(rule, value) && !tooLong(rule, value);
    }

    function isValidExperienceRequest(experience: EditExperience): boolean {
        return (
            // Mandatory fields
            isValidExperienceTitle(experience.title) &&
            validateExperienceCompany(experience.company) &&
            isValidExperienceDateRange(experience.startDate, experience.endDate, experience.currentJob) &&
            validateExperienceDescription(experience.description)
        );
    }

    return {
        // RO
        alternativeAddressesCount: readonlyState(alternativeAddressesCount),
        categoryOther: readonlyState(categoryOther),
        certificationSkills: readonlyState(certificationSkills),
        dailyRatePrice: readonlyState(dailyRatePrice),
        educationCountForMaltStrategy: readonlyState(educationCountForMaltStrategy),
        educationDegreeLength: readonlyState(educationDegreeLength),
        educationSchoolLength: readonlyState(educationSchoolLength),
        educationGraduationYear: readonlyState(educationGraduationYear),
        experienceDescriptionLength: readonlyState(experienceDescriptionLength),
        experienceTitleLength: readonlyState(experienceTitleLength),
        experienceClientCompanyLength: readonlyState(experienceClientCompanyLength),
        experienceMinCount: readonlyState(experienceMinCount),
        experienceMaxSkills: readonlyState(experienceMaxSkills),
        industryExpertisesCount: readonlyState(industryExpertisesCount),
        jobTitle: readonlyState(jobTitle),
        languageCount: readonlyState(languageCount),
        portfolioDescriptionLength: readonlyState(portfolioDescriptionLength),
        portfolioMaxSkills: readonlyState(portfolioMaxSkills),
        portfolioTitleLength: readonlyState(portfolioTitleLength),
        profilePictureFileSize: readonlyState(profilePictureFileSize),
        profilePictureFormats: readonlyState(profilePictureFormats),
        profilePictureWidthHeight: readonlyState(profilePictureWidthHeight),
        resumeDescriptionLength: readonlyState(resumeDescriptionLength),
        skillSetFunctionalSkills: readonlyState(skillSetFunctionalSkills),
        skillSetGlobalSkillLabel: readonlyState(skillSetGlobalSkillLabel),
        skillSetGlobalSkills: readonlyState(skillSetGlobalSkills),
        skillSetSelectedSkills: readonlyState(skillSetSelectedSkills),
        skillSetTopSkills: readonlyState(skillSetTopSkills),
        skillSetSkills: readonlyState(skillSetSkills),
        travelLocationsCount: readonlyState(travelLocationsCount),
        experienceFixedDateMin: experienceFixedDateMin,
        experienceFixedDateMax: experienceFixedDateMax,
        isValidExperienceRequest,
        validateExperienceDescription,
        isValidExperienceDateRange,
        // functions
        init,
    };
});
